import { useState, useMemo } from "react";
import { ExternalLink, ChevronDown, ChevronUp, Maximize2, Loader2 } from "lucide-react";
import { useWidgetProps } from "../use-widget-props";
import { useTheme } from "../use-theme";
import { useDisplayMode } from "../use-display-mode";
import { useWidgetState } from "../use-widget-state";

type Product = {
  id: string;
  name: string;
  brand: string;
  price: number;
  currency: string;
  image: string;
  url: string;
  retailer: string;
  score?: number;
};

type ToolOutput = {
  summary: string;
  searchedProduct: Product;
  alternatives: Product[];
  totalAlternatives: number;
  _streaming?: boolean;
};

type SortOption = "relevance" | "price-low" | "price-high";

function formatBrand(brand: string): string {
  return brand
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatPrice(price: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
  }).format(price);
}

function ProductCard({ product, isDark, onClick }: { product: Product; isDark: boolean; onClick: () => void }) {
  return (
    <article
      onClick={onClick}
      className="group cursor-pointer flex flex-col overflow-hidden transition-all"
    >
      <div className={`relative aspect-square overflow-hidden rounded-2xl border ${
        isDark ? "border-white/10 bg-gray-800" : "border-black/5 bg-gray-100"
      }`}>
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          onError={(e) => {
            (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&h=400&fit=crop";
          }}
        />
      </div>

      <div className="flex-1 pt-3 flex flex-col gap-1">
        {product.retailer && (
          <p className={`text-sm tracking-[-0.3px] ${isDark ? "text-[#8f8f8f]" : "text-[#8f8f8f]"}`}>
            {formatBrand(product.retailer).toUpperCase()}
          </p>
        )}
        <h3 className={`font-medium text-[17px] leading-tight tracking-[-0.43px] ${isDark ? "text-white" : "text-[#0d0d0d]"}`}>
          {formatBrand(product.brand)}
        </h3>
        <p className={`text-sm tracking-[-0.3px] line-clamp-2 ${isDark ? "text-[#8f8f8f]" : "text-[#5d5d5d]"}`}>
          {product.name}
        </p>
        <p className={`text-sm tracking-[-0.3px] ${isDark ? "text-white" : "text-[#0d0d0d]"}`}>
          {formatPrice(product.price, product.currency)}
        </p>
      </div>
    </article>
  );
}

function LoadingState({ isDark }: { isDark: boolean }) {
  return (
    <div className="w-full">
      <div className={`p-8 flex flex-col items-center justify-center gap-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
        <Loader2 className="w-8 h-8 animate-spin" />
        <p className="text-sm font-medium">Searching for alternatives...</p>
      </div>
    </div>
  );
}

export default function App() {
  const props = useWidgetProps<ToolOutput>();
  const theme = useTheme() ?? "light";
  const displayMode = useDisplayMode() ?? "inline";
  const isDark = theme === "dark";
  const isFullscreen = displayMode === "fullscreen";

  const [, setWidgetState] = useWidgetState({ selectedId: null as string | null });
  const [sortBy, setSortBy] = useState<SortOption>("relevance");
  const [showAllAlternatives, setShowAllAlternatives] = useState(false);

  const sortedAlternatives = useMemo(() => {
    if (!props?.alternatives) return [];
    const alternatives = [...props.alternatives];
    switch (sortBy) {
      case "price-low":
        return alternatives.sort((a, b) => a.price - b.price);
      case "price-high":
        return alternatives.sort((a, b) => b.price - a.price);
      case "relevance":
      default:
        return alternatives.sort((a, b) => (b.score || 0) - (a.score || 0));
    }
  }, [props?.alternatives, sortBy]);

  if (!props || !props.searchedProduct) {
    return <LoadingState isDark={isDark} />;
  }

  const displayedAlternatives = showAllAlternatives
    ? sortedAlternatives
    : sortedAlternatives.slice(0, isFullscreen ? 12 : 8);

  const hasMoreAlternatives = sortedAlternatives.length > displayedAlternatives.length;

  const handleOpenProduct = (id: string, url: string) => {
    setWidgetState({ selectedId: id });
    if (window.openai?.openExternal) {
      window.openai?.openExternal({ href: url });
    } else {
      window.open(url, "_blank");
    }
  };

  const handleRequestFullscreen = async () => {
    if (window.openai?.requestDisplayMode) {
      await window.openai?.requestDisplayMode({ mode: "fullscreen" });
    } else if (window.webplus?.requestDisplayMode) {
      await window.webplus?.requestDisplayMode({ mode: "fullscreen" });
    }
  };

  const cheaperCount = props.alternatives.filter(
    (alt) => alt.price < props.searchedProduct.price
  ).length;

  return (
    <div className={`w-full overflow-hidden ${isDark ? "bg-[#0d0d0d]" : "bg-white"}`}>
      <span className="sr-only">{props.summary}</span>
      {/* Header with original product */}
      <div className="pb-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-[22px] font-semibold tracking-[-0.43px] ${isDark ? "text-white" : "text-[#0d0d0d]"}`}>
            Original Piece
          </h2>
          <div className="flex gap-1">
            {!isFullscreen && (
              <button
                onClick={handleRequestFullscreen}
                className={`p-2 rounded-full transition-colors ${
                  isDark ? "hover:bg-white/10 text-gray-400" : "hover:bg-gray-100 text-[#5d5d5d]"
                }`}
                aria-label="Enter fullscreen"
              >
                <Maximize2 className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={() => handleOpenProduct(props.searchedProduct.id, props.searchedProduct.url)}
              className={`p-2 rounded-full transition-colors ${
                isDark ? "hover:bg-white/10 text-gray-400" : "hover:bg-gray-100 text-[#5d5d5d]"
              }`}
              aria-label="View original product"
            >
              <ExternalLink className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="flex gap-4">
          <div className={`flex-shrink-0 w-[100px] h-[84px] rounded-xl overflow-hidden border ${
            isDark ? "border-white/10 bg-gray-800" : "border-black/5 bg-[#f8f7f5]"
          }`}>
            <img
              src={props.searchedProduct.image}
              alt={props.searchedProduct.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&h=400&fit=crop";
              }}
            />
          </div>

          <div className="flex-1 min-w-0">
            {props.searchedProduct.retailer && (
              <p className={`text-sm tracking-[-0.43px] ${isDark ? "text-[#8f8f8f]" : "text-[#5d5d5d]"}`}>
                {formatBrand(props.searchedProduct.retailer).toUpperCase()}
              </p>
            )}
            <h3 className={`font-semibold text-[17px] tracking-[-0.43px] ${isDark ? "text-white" : "text-[#0d0d0d]"}`}>
              {formatBrand(props.searchedProduct.brand)}
            </h3>
            <p className={`text-[17px] font-medium tracking-[-0.43px] truncate ${isDark ? "text-[#8f8f8f]" : "text-[#5d5d5d]"}`}>
              {props.searchedProduct.name}
            </p>
            <p className={`text-sm tracking-[-0.3px] mt-1 ${isDark ? "text-[#8f8f8f]" : "text-[#5d5d5d]"}`}>
              {formatPrice(props.searchedProduct.price, props.searchedProduct.currency)}
            </p>

            <div className="flex gap-4 mt-2">
              <span className={`text-xs ${isDark ? "text-gray-400" : "text-[#5d5d5d]"}`}>
                {props.totalAlternatives} similar finds
              </span>
              {cheaperCount > 0 && (
                <span className="text-xs text-green-600 font-medium">
                  {cheaperCount} cheaper options
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sort controls */}
      <div className="py-3 flex items-center justify-between">
        <h3 className={`text-[22px] font-semibold tracking-[-0.43px] ${isDark ? "text-white" : "text-[#0d0d0d]"}`}>
          Similar Finds
        </h3>
        <div className="flex items-center gap-2">
          <label className={`text-xs ${isDark ? "text-gray-400" : "text-[#5d5d5d]"}`}>Sort by:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className={`text-xs rounded-lg px-2 py-1 border ${
              isDark
                ? "bg-gray-800 border-gray-700 text-white"
                : "bg-white border-gray-200 text-gray-900"
            }`}
          >
            <option value="relevance">Most Similar</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
          </select>
        </div>
      </div>

      {/* Alternatives grid */}
      <div className="pt-2 pb-4">
        {displayedAlternatives.length > 0 ? (
          <>
            <div className={`grid gap-4 ${isFullscreen ? "grid-cols-4" : "grid-cols-3"}`}>
              {displayedAlternatives.map((alt) => (
                <ProductCard
                  key={alt.id}
                  product={alt}
                  isDark={isDark}
                  onClick={() => handleOpenProduct(alt.id, alt.url)}
                />
              ))}
            </div>

            {(hasMoreAlternatives || showAllAlternatives) && sortedAlternatives.length > (isFullscreen ? 12 : 8) && (
              <button
                onClick={() => setShowAllAlternatives(!showAllAlternatives)}
                className={`w-full mt-4 py-3 rounded-xl border flex items-center justify-center gap-2 text-sm font-medium transition-colors ${
                  isDark
                    ? "border-gray-700 text-gray-300 hover:bg-gray-800"
                    : "border-gray-200 text-gray-700 hover:bg-gray-100"
                }`}
              >
                {showAllAlternatives ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Show All {sortedAlternatives.length} Similar Finds
                  </>
                )}
              </button>
            )}
          </>
        ) : props._streaming ? (
          <div className={`flex flex-col items-center justify-center py-8 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            <Loader2 className="w-6 h-6 animate-spin mb-2 opacity-50" />
            <p className="text-sm">Finding similar products...</p>
          </div>
        ) : (
          <div className={`text-center py-8 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            <p>No alternatives found for this product.</p>
          </div>
        )}
      </div>
    </div>
  );
}
